import React from 'react';
import PropTypes from 'prop-types';
import { PRIVACY, TERMS, BUILDER_POLICY, REFUND, ReorderLogNote } from './companyLegal.jsx';

// A readable legal page for templates that do not ship one of their own.
//
// The legal routes (cookie, terms, privacy, shipping, income) are dispatched
// to the template's own `Legal` component. Only two templates have one, so on
// every other store those URLs quietly rendered the HOME page — which is what
// the cookie-consent banner's "cookie policy" link landed on.
//
// This is the fallback: same md2 keys the template versions read, so a store
// that has already written its policy keeps it and the admin fields keep
// working, with built-in text when nothing has been written. It inherits the
// template's Shell, so header, footer and theme are the store's own; only the
// body is neutral.

const KEYS = {
  terms: { title: 'tmTitle', intro: 'tmIntro', list: 'tmSections' },
  cookie: { title: 'ckTitle', intro: 'ckIntro', list: 'ckSections' },
  shipping: { title: 'shTitle', intro: 'shIntro', list: 'shSections' },
  income: { title: 'idTitle', intro: 'idIntro', list: 'idSections' },
  privacy: { title: 'pvTitle', intro: 'pvIntro', list: 'pvSections' },
  'builder-policy': { title: 'bpTitle', intro: 'bpIntro', list: 'bpSections' },
  // Same keys the Adapt X.1 template's own Refund page edits (rf*), so the
  // admin fields are shared.
  refund: { title: 'rfTitle', intro: 'rfIntro', list: 'rfSections' },
};

// Written to describe what this storefront's banner ACTUALLY does — Google
// Consent Mode v2, with advertising and email tags held back until the visitor
// accepts — rather than boilerplate that promises something the code does not
// do. Any store can replace all of it from the admin.
const DEFAULTS = {
  // Company-wide New Deal policies (companyLegal.jsx).
  privacy: PRIVACY,
  terms: TERMS,
  'builder-policy': BUILDER_POLICY,
  refund: REFUND,
  cookie: {
    title: 'Cookie Policy',
    intro: 'This policy explains the cookies and similar technologies this store uses, what they are for, and how you can change your mind at any time.',
    sections: [
      { h: 'Cookies we cannot do without', b: 'A small number of cookies and local storage entries are needed for the store to work at all: keeping your basket between pages, remembering that you are signed in, and carrying your cookie choice so we do not ask again on every visit. These are always active — without them the store cannot function.' },
      { h: 'Measurement', b: 'Where analytics are enabled, we use them to count visits and understand which pages people use, so we can improve the store. Until you accept, these tools are held in a restricted mode that does not write identifying cookies.' },
      { h: 'Advertising and email', b: 'Where advertising or email tools are enabled, they help us show relevant ads and measure whether an ad led to a purchase. These never load until you press Accept.' },
      { h: 'Your choice', b: 'The banner shown on your first visit offers Accept and Decline. Decline means advertising and email tools are not loaded at all, and measurement stays in its restricted, cookieless mode. Your choice is stored on your own device for this store.' },
      { h: 'Changing your mind', b: 'Clearing this site’s cookies and site data in your browser removes your stored choice, and the banner will appear again on your next visit so you can choose differently. You can also block or delete cookies entirely in your browser settings — parts of the store, such as the basket, may then stop working.' },
      { h: 'Third parties', b: 'When these tools are active, the providers behind them receive data according to their own privacy policies. We do not sell your personal information.' },
    ],
  },
};

const LegalFallback = ({ skin, page }) => {
  const key = KEYS[page] ? page : 'privacy';
  const cfg = KEYS[key];
  const t = skin?.t ?? {};
  const dark = t.dark === true;
  const ink = dark ? '#fff' : (t.ink ?? '#16181d');
  const body = dark ? 'rgba(255,255,255,.76)' : (t.muted ?? '#54514b');

  const md2 = skin?.md2 ?? {};
  const text = (k, fallback) => {
    const v = typeof md2.text === 'function' ? md2.text(k) : (md2.texts ?? {})[k];
    return (v === undefined || v === null || v === '') ? fallback : v;
  };
  const listed = (md2.lists ?? {})[cfg.list];
  const d = DEFAULTS[key] ?? {};
  const sections = Array.isArray(listed) && listed.length ? listed : (d.sections ?? []);

  return (
    <div style={{ maxWidth: 820, margin: '0 auto', padding: '70px 20px 90px', color: ink }}>
      <h1 style={{ fontSize: 'clamp(28px,4vw,42px)', margin: 0, lineHeight: 1.15 }}>{text(cfg.title, d.title ?? 'Policy')}</h1>
      <p style={{ fontSize: 15, color: body, lineHeight: 1.75, marginTop: 16, maxWidth: '68ch' }}>{text(cfg.intro, d.intro ?? '')}</p>
      <ReorderLogNote dark={dark} accent={t.accent} />
      <div style={{ marginTop: 30 }}>
        {sections.map((s, i) => (
          <div key={i} style={{ marginBottom: 26 }}>
            <h2 style={{ fontSize: 19, margin: '0 0 8px', lineHeight: 1.3 }}>{s.h}</h2>
            <p style={{ fontSize: 14.5, color: body, lineHeight: 1.75, margin: 0, maxWidth: '68ch' }}>{s.b}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

LegalFallback.propTypes = { skin: PropTypes.object, page: PropTypes.string };

export default LegalFallback;
